"use client";

import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AnimatedCard } from "@/components/motion/AnimatedCard";
import { Button } from "@/components/ui/Button";
import { Check, Tag } from "@/components/ui/icons";
import { LINKS, PRICING, PRODUCTS, priceLabel } from "@/content/quikit";
import { cn } from "@/lib/utils";
import { DarkPanel } from "./DarkPanel";
import { SectionIntro } from "./SectionIntro";

const VIEWS = ["Pricing models", "App prices"] as const;
const EASE = [0.23, 1, 0.32, 1] as const;
const PAID_APPS = PRODUCTS.filter((p) => p.family !== "QuikOffice");

/** Final dark pricing chapter: per-app models or the full price list, then the start-small band. */
export function Pricing() {
  const [view, setView] = useState<(typeof VIEWS)[number]>("Pricing models");

  return (
    <DarkPanel
      id="pricing"
      ariaLabelledby="pricing-title"
      threads={[8, 17, 31, 40, 83]}
      className="mt-[clamp(120px,12vw,190px)]"
      panelClassName="pb-[clamp(28px,4.4vw,64px)] pt-[clamp(64px,7vw,104px)]"
    >
      <div className="wrap">
        <SectionIntro
          tone="dark"
          eyebrow={PRICING.eyebrow}
          eyebrowIcon={<Tag className="size-3" />}
          title={PRICING.title}
          titleId="pricing-title"
          text={PRICING.text}
          textClassName="max-w-[52ch]"
        />

        <div className="mt-8 flex justify-center">
          <div role="tablist" aria-label="Pricing view" className="flex rounded-full bg-white/[0.06] p-1 ring-1 ring-inset ring-white/10">
            {VIEWS.map((v) => (
              <button
                key={v}
                role="tab"
                type="button"
                aria-selected={view === v}
                onClick={() => setView(v)}
                className="relative h-8 rounded-full px-4 text-[12px] font-medium"
              >
                {view === v && (
                  <motion.span layoutId="price-view" className="absolute inset-0 rounded-full bg-white" transition={{ type: "spring", duration: 0.45, bounce: 0.14 }} />
                )}
                <span className={cn("relative transition-colors", view === v ? "text-ink" : "text-white/60")}>{v}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="mt-10 md:mt-12">
          <AnimatePresence mode="wait" initial={false}>
            {view === "Pricing models" ? (
              <motion.div key="models" exit={{ opacity: 0, filter: "blur(6px)", transition: { duration: 0.25 } }} className="grid gap-2 md:grid-cols-3">
                {PRICING.models.map((m, i) => (
                  <AnimatedCard
                    key={m.name}
                    index={i}
                    y={70}
                    cascade={24}
                    stagger={0.1}
                    start="top 92%"
                    className="flex flex-col rounded-[20px] border border-white/[0.08] bg-[#0f0f10] p-6"
                    style={{ backgroundImage: m.glow || undefined }}
                  >
                    <h3 className="text-[24px] font-[450] tracking-[-0.03em]">{m.name}</h3>
                    <p className="mt-3 text-[14px] leading-[1.6] text-white/50">{m.text}</p>
                    <p className="mt-auto pt-8 text-[12px] text-white/45">From</p>
                    <p className="text-[30px] font-medium tracking-[-0.03em]">
                      {m.from} <span className="text-[12px] font-normal text-white/45">{m.unit}</span>
                    </p>
                  </AnimatedCard>
                ))}
              </motion.div>
            ) : (
              <motion.ul
                key="apps"
                initial={{ opacity: 0, filter: "blur(6px)", y: 20 }}
                animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
                exit={{ opacity: 0, filter: "blur(6px)", transition: { duration: 0.25 } }}
                transition={{ duration: 0.6, ease: EASE }}
                className="grid gap-x-8 rounded-[20px] border border-white/[0.08] bg-[#0f0f10] p-5 sm:grid-cols-2 md:p-6 lg:grid-cols-3"
              >
                {PAID_APPS.map((p) => (
                  <li key={p.key} className="flex items-center justify-between gap-3 border-b border-white/[0.06] py-3 text-[14px]">
                    <span className="flex items-center gap-2">
                      {p.name}
                      {p.status === "soon" && <span className="rounded-full bg-white/[0.08] px-1.5 py-px text-[9.5px] text-white/50">Soon</span>}
                    </span>
                    <span className="shrink-0 text-right text-white/60">{priceLabel(p)}</span>
                  </li>
                ))}
              </motion.ul>
            )}
          </AnimatePresence>
        </div>

        {/* Start where you are */}
        <div className="mt-4 grid gap-4 rounded-[24px] border border-white/[0.07] bg-[linear-gradient(180deg,#171718_0%,#101011_100%)] p-5 md:grid-cols-[1.2fr_1fr] md:p-8">
          <div>
            <p className="text-[11.5px] font-medium uppercase tracking-[0.12em] text-mint">Start where you are</p>
            <h3 className="mt-3 max-w-[14em] text-[clamp(1.6rem,2.3vw,2.1rem)] font-[450] leading-[1.15] tracking-[-0.035em]">
              Start with one app. Grow into the suite.
            </h3>
            <ol className="mt-7 space-y-4">
              {PRICING.steps.map(([title, text], i) => (
                <li key={title} className="flex gap-4">
                  <span className="text-[12px] font-medium tabular-nums text-mint">0{i + 1}</span>
                  <span>
                    <span className="block text-[14px] font-medium">{title}</span>
                    <span className="block text-[14px] text-white/50">{text}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
          <div className="rounded-[20px] bg-white p-6 text-ink">
            <p className="text-[12px] font-medium text-muted">Pricing per app</p>
            <p className="mt-2 flex items-baseline gap-1.5">
              <span className="text-[12px] text-muted">From</span>
              <span className="text-[44px] font-medium leading-none tracking-[-0.04em]">$1</span>
              <span className="text-[12px] text-muted">/ user / month</span>
            </p>
            <hr className="my-5 border-line" />
            <ul className="space-y-2.5">
              {PRICING.includes.map((t) => (
                <li key={t} className="flex items-center gap-2 text-[14px]">
                  <Check className="size-4 text-accent" strokeWidth={2.2} />
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-6 flex flex-wrap gap-2">
              <Button href={LINKS.register}>Start free trial</Button>
              <Button variant="outline" href={LINKS.demo}>
                Talk to sales
              </Button>
            </div>
          </div>
        </div>
        <p className="mt-6 text-center text-[12px] text-white/40">{PRICING.note}</p>
      </div>
    </DarkPanel>
  );
}
